import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import {
    API_BASE_URL,
    deleteRequest,
    extractErrorMessage,
    getRequest,
    postRequest,
    putRequest,
} from "../../../api";
import {
    changeKmsDocumentStatusFail,
    changeKmsDocumentStatusSuccess,
    archiveKmsDocumentFail,
    createKmsDocumentCommentFail,
    createKmsDocumentCommentSuccess,
    deleteKmsDocumentCommentFail,
    deleteKmsDocumentCommentSuccess,
    deleteKmsDocumentFail,
    deleteKmsDocumentSuccess,
    getKmsArchivedDocumentsFail,
    getKmsArchivedDocumentsSuccess,
    getKmsDocumentFail,
    getKmsDocumentSuccess,
    getKmsDocumentCommentsFail,
    getKmsDocumentCommentsSuccess,
    getKmsDocumentRatingSummaryFail,
    getKmsDocumentRatingSummarySuccess,
    getKmsDocumentSubscriptionFail,
    getKmsDocumentSubscriptionSuccess,
    getKmsDocumentsFail,
    getKmsDocumentsSuccess,
    getKmsDocumentsByContentFail,
    getKmsDocumentsByContentSuccess,
    getKmsDocumentsByRepertoireFail,
    getKmsDocumentsByRepertoireSuccess,
    getKmsTrashedDocumentsFail,
    getKmsTrashedDocumentsSuccess,
    hardDeleteKmsDocumentFail,
    hardDeleteKmsDocumentSuccess,
    restoreKmsDocumentFromArchiveFail,
    restoreKmsDocumentFromArchiveSuccess,
    restoreKmsDocumentFromTrashFail,
    restoreKmsDocumentFromTrashSuccess,
    setKmsDocumentDetailLoading,
    setKmsDocumentListLoading,
    setKmsDocumentSaveLoading,
    subscribeKmsDocumentFail,
    subscribeKmsDocumentSuccess,
    unsubscribeKmsDocumentFail,
    unsubscribeKmsDocumentSuccess,
    upsertKmsDocumentRatingFail,
    upsertKmsDocumentRatingSuccess,
    uploadKmsDocumentToContentFail,
    uploadKmsDocumentToContentSuccess,
    uploadKmsDocumentToRepertoireFail,
    uploadKmsDocumentToRepertoireSuccess,
    updateKmsDocumentFail,
    updateKmsDocumentSuccess,
} from "../actions/kmsDocumentActions";

const getUidHeaderAndToken = () => ({
    uid: sessionStorage.getItem("uid") || localStorage.getItem("uid"),
    token: sessionStorage.getItem("token") || localStorage.getItem("token"),
});

function buildDocumentFormData(payload) {
    const formData = new FormData();
    const file = payload?.file;
    const clean = {
        title: payload?.title || "",
        description: payload?.description || "",
        categoryId: payload?.categoryId || null,
        tagIds: payload?.tagIds || [],
        documentType: payload?.documentType || null,
    };
    formData.append("payload", JSON.stringify(clean));
    formData.append("file", file);
    return formData;
}

const buildWorkflowBody = (comment) => ({ comment: comment || "" });

function* refreshDocumentCollections(document) {
    if (!document) return;
    if (document.repertoireId) {
        yield put({ type: "GET_KMS_DOCUMENTS_BY_REPERTOIRE_REQUEST", payload: { repertoireId: document.repertoireId } });
    }
    if (document.contentId) {
        yield put({ type: "GET_KMS_DOCUMENTS_BY_CONTENT_REQUEST", payload: { contentId: document.contentId } });
    }
    yield put({ type: "GET_KMS_DOCUMENTS_REQUEST", payload: {} });
}

export function* getKmsDocumentsSaga(action) {
    const { token } = getUidHeaderAndToken();
    const params = new URLSearchParams();
    const filters = action.payload || {};

    if (filters.repertoireId) params.set("repertoireId", filters.repertoireId);
    if (filters.contentId) params.set("contentId", filters.contentId);
    if (filters.documentType) params.set("documentType", filters.documentType);

    const query = params.toString() ? `?${params.toString()}` : "";
    const url = `${API_BASE_URL}/api/kms/documents${query}`;

    yield put(setKmsDocumentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentsSuccess(resp.data, filters));
    } catch (error) {
        yield put(getKmsDocumentsFail(extractErrorMessage(error, "Erreur de chargement des documents")));
    } finally {
        yield put(setKmsDocumentListLoading(false));
    }
}

export function* getKmsDocumentsByRepertoireSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { repertoireId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/repertoire/${repertoireId}`;

    yield put(setKmsDocumentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentsByRepertoireSuccess(repertoireId, resp.data));
    } catch (error) {
        yield put(getKmsDocumentsByRepertoireFail(extractErrorMessage(error, "Erreur de chargement des documents")));
    } finally {
        yield put(setKmsDocumentListLoading(false));
    }
}

export function* getKmsArchivedDocumentsSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/kms/documents/archive`;

    yield put(setKmsDocumentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsArchivedDocumentsSuccess(resp.data));
    } catch (error) {
        yield put(getKmsArchivedDocumentsFail(extractErrorMessage(error, "Erreur de chargement des documents archives")));
    } finally {
        yield put(setKmsDocumentListLoading(false));
    }
}

export function* getKmsTrashedDocumentsSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/kms/documents/trash`;

    yield put(setKmsDocumentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsTrashedDocumentsSuccess(resp.data));
    } catch (error) {
        yield put(getKmsTrashedDocumentsFail(extractErrorMessage(error, "Erreur de chargement des documents en corbeille")));
    } finally {
        yield put(setKmsDocumentListLoading(false));
    }
}

export function* getKmsDocumentsByContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/content/${contentId}`;

    yield put(setKmsDocumentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentsByContentSuccess(contentId, resp.data));
    } catch (error) {
        yield put(getKmsDocumentsByContentFail(extractErrorMessage(error, "Erreur de chargement des documents")));
    } finally {
        yield put(setKmsDocumentListLoading(false));
    }
}

export function* getKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}`;

    yield put(setKmsDocumentDetailLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentSuccess(resp.data));
    } catch (error) {
        yield put(getKmsDocumentFail(extractErrorMessage(error, "Erreur de chargement du document")));
    } finally {
        yield put(setKmsDocumentDetailLoading(false));
    }
}

export function* getKmsDocumentCommentsSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/comments`;

    yield put({
        type: "SET_KMS_DOCUMENT_COMMENTS_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentCommentsSuccess(documentId, resp.data));
    } catch (error) {
        yield put(getKmsDocumentCommentsFail(documentId, extractErrorMessage(error, "Erreur de chargement des commentaires document")));
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_COMMENTS_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* getKmsDocumentSubscriptionSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/subscription`;

    yield put({
        type: "SET_KMS_DOCUMENT_SUBSCRIPTION_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentSubscriptionSuccess(documentId, resp.data));
    } catch (error) {
        yield put(getKmsDocumentSubscriptionFail(documentId, extractErrorMessage(error, "Erreur de chargement de l'abonnement document")));
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_SUBSCRIPTION_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* getKmsDocumentRatingSummarySaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/ratings/summary`;

    yield put({
        type: "SET_KMS_DOCUMENT_RATING_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsDocumentRatingSummarySuccess(documentId, resp.data));
    } catch (error) {
        yield put(getKmsDocumentRatingSummaryFail(documentId, extractErrorMessage(error, "Erreur de chargement des évaluations document")));
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_RATING_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* createKmsDocumentCommentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId, message } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/comments`;

    yield put({
        type: "SET_KMS_DOCUMENT_COMMENT_SAVE_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        yield call(postRequest, { url, data: { message }, token });
        yield put(createKmsDocumentCommentSuccess(documentId));
        yield put({ type: "GET_KMS_DOCUMENT_COMMENTS_REQUEST", payload: { documentId } });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de l'ajout du commentaire document");
        yield put(createKmsDocumentCommentFail(documentId, messageError));
        yield call([Swal, Swal.fire], { icon: "error", text: messageError, confirmButtonText: "OK" });
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_COMMENT_SAVE_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* subscribeKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/subscription`;

    yield put({
        type: "SET_KMS_DOCUMENT_SUBSCRIPTION_SAVE_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        const resp = yield call(postRequest, { url, data: {}, token });
        yield put(subscribeKmsDocumentSuccess(documentId, resp.data));
        yield put({ type: "GET_KMS_DOCUMENT_SUBSCRIPTION_REQUEST", payload: { documentId } });
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Abonnement au document enregistré",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de l'abonnement au document");
        yield put(subscribeKmsDocumentFail(documentId, messageError));
        yield call([Swal, Swal.fire], { icon: "error", text: messageError, confirmButtonText: "OK" });
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_SUBSCRIPTION_SAVE_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* unsubscribeKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/subscription`;

    yield put({
        type: "SET_KMS_DOCUMENT_SUBSCRIPTION_SAVE_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        const resp = yield call(deleteRequest, { url, token });
        yield put(unsubscribeKmsDocumentSuccess(documentId, resp.data));
        yield put({ type: "GET_KMS_DOCUMENT_SUBSCRIPTION_REQUEST", payload: { documentId } });
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Désabonnement du document enregistré",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors du désabonnement du document");
        yield put(unsubscribeKmsDocumentFail(documentId, messageError));
        yield call([Swal, Swal.fire], { icon: "error", text: messageError, confirmButtonText: "OK" });
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_SUBSCRIPTION_SAVE_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* upsertKmsDocumentRatingSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId, rating } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/ratings`;

    yield put({
        type: "SET_KMS_DOCUMENT_RATING_SAVE_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        const resp = yield call(postRequest, { url, data: { rating }, token });
        yield put(upsertKmsDocumentRatingSuccess(documentId, resp.data));
        yield put({ type: "GET_KMS_DOCUMENT_RATING_SUMMARY_REQUEST", payload: { documentId } });
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Votre évaluation a été enregistrée",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de l'enregistrement de l'évaluation document");
        yield put(upsertKmsDocumentRatingFail(documentId, messageError));
        yield call([Swal, Swal.fire], { icon: "error", text: messageError, confirmButtonText: "OK" });
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_RATING_SAVE_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* deleteKmsDocumentCommentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId, commentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/comments/${commentId}`;

    yield put({
        type: "SET_KMS_DOCUMENT_COMMENT_SAVE_LOADING",
        payload: { documentId, loading: true },
    });
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteKmsDocumentCommentSuccess(documentId));
        yield put({ type: "GET_KMS_DOCUMENT_COMMENTS_REQUEST", payload: { documentId } });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de la suppression du commentaire document");
        yield put(deleteKmsDocumentCommentFail(documentId, messageError));
        yield call([Swal, Swal.fire], { icon: "error", text: messageError, confirmButtonText: "OK" });
    } finally {
        yield put({
            type: "SET_KMS_DOCUMENT_COMMENT_SAVE_LOADING",
            payload: { documentId, loading: false },
        });
    }
}

export function* uploadKmsDocumentToRepertoireSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { repertoireId, payload } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/upload/repertoire/${repertoireId}`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const data = buildDocumentFormData(payload);
        const resp = yield call(postRequest, { url, data, token });
        yield put(uploadKmsDocumentToRepertoireSuccess(resp.data));
        yield put({ type: "GET_KMS_DOCUMENTS_BY_REPERTOIRE_REQUEST", payload: { repertoireId } });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document ajoute au repertoire",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de l'upload du document");
        yield put(uploadKmsDocumentToRepertoireFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* uploadKmsDocumentToContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId, payload } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/upload/content/${contentId}`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const data = buildDocumentFormData(payload);
        const resp = yield call(postRequest, { url, data, token });
        yield put(uploadKmsDocumentToContentSuccess(resp.data));
        yield put({ type: "GET_KMS_DOCUMENTS_BY_CONTENT_REQUEST", payload: { contentId } });
        if (resp.data?.repertoireId) {
            yield put({ type: "GET_KMS_DOCUMENTS_BY_REPERTOIRE_REQUEST", payload: { repertoireId: resp.data.repertoireId } });
        }

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document ajoute au contenu",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de l'upload du document");
        yield put(uploadKmsDocumentToContentFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* updateKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId, payload } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const data = buildDocumentFormData(payload);
        const resp = yield call(putRequest, { url, data, token });
        yield put(updateKmsDocumentSuccess(resp.data));
        yield* refreshDocumentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document mis à jour avec succès",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la mise à jour du document");
        yield put(updateKmsDocumentFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* executeKmsDocumentWorkflowSaga(action, endpoint, successMessage, defaultErrorMessage) {
    const { token } = getUidHeaderAndToken();
    const { documentId, comment } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/workflow/${endpoint}`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const resp = yield call(postRequest, { url, data: buildWorkflowBody(comment), token });
        yield put(changeKmsDocumentStatusSuccess(resp.data));
        yield* refreshDocumentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: successMessage,
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, defaultErrorMessage);
        yield put(changeKmsDocumentStatusFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* submitKmsDocumentSaga(action) {
    yield* executeKmsDocumentWorkflowSaga(action, "submit", "Document soumis pour validation", "Erreur lors de la soumission du document");
}

export function* approveKmsDocumentSaga(action) {
    yield* executeKmsDocumentWorkflowSaga(action, "approve", "Document approuve avec succes", "Erreur lors de l'approbation du document");
}

export function* rejectKmsDocumentSaga(action) {
    yield* executeKmsDocumentWorkflowSaga(action, "reject", "Document rejete avec succes", "Erreur lors du rejet du document");
}

export function* returnKmsDocumentToDraftSaga(action) {
    yield* executeKmsDocumentWorkflowSaga(action, "return-for-correction", "Document retourné pour correction", "Erreur lors du retour pour correction");
}

export function* publishKmsDocumentSaga(action) {
    yield* executeKmsDocumentWorkflowSaga(action, "publish", "Document publié avec succes", "Erreur lors de la publication du document");
}

export function* archiveKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/archive`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const resp = yield call(putRequest, { url, data: {}, token });
        yield put(changeKmsDocumentStatusSuccess(resp.data));
        yield* refreshDocumentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document archivé avec succes",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de l'archivage du document");
        yield put(archiveKmsDocumentFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* deleteKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId, repertoireId, contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteKmsDocumentSuccess(documentId, repertoireId, contentId));
        if (repertoireId) {
            yield put({ type: "GET_KMS_DOCUMENTS_BY_REPERTOIRE_REQUEST", payload: { repertoireId } });
        }
        if (contentId) {
            yield put({ type: "GET_KMS_DOCUMENTS_BY_CONTENT_REQUEST", payload: { contentId } });
        }

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document supprimé avec succès",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la suppression du document");
        yield put(deleteKmsDocumentFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* restoreKmsDocumentFromArchiveSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/restore-archive`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const resp = yield call(putRequest, { url, data: {}, token });
        yield put(restoreKmsDocumentFromArchiveSuccess(resp.data));
        yield put({ type: "GET_KMS_ARCHIVED_DOCUMENTS_REQUEST" });
        yield put({ type: "GET_KMS_DOCUMENTS_REQUEST", payload: {} });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document restauré depuis les archives",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la restauration du document archive");
        yield put(restoreKmsDocumentFromArchiveFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* restoreKmsDocumentFromTrashSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/restore-trash`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        const resp = yield call(putRequest, { url, data: {}, token });
        yield put(restoreKmsDocumentFromTrashSuccess(resp.data));
        yield put({ type: "GET_KMS_TRASHED_DOCUMENTS_REQUEST" });
        yield put({ type: "GET_KMS_DOCUMENTS_REQUEST", payload: {} });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document restauré depuis la corbeille",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la restauration du document supprimé");
        yield put(restoreKmsDocumentFromTrashFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}

export function* hardDeleteKmsDocumentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { documentId, source } = action.payload;
    const url = `${API_BASE_URL}/api/kms/documents/${documentId}/hard-delete`;

    yield put(setKmsDocumentSaveLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(hardDeleteKmsDocumentSuccess(documentId, source));
        yield put({ type: source === "archive" ? "GET_KMS_ARCHIVED_DOCUMENTS_REQUEST" : "GET_KMS_TRASHED_DOCUMENTS_REQUEST" });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Document supprimé définitivement",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la suppression definitive du document");
        yield put(hardDeleteKmsDocumentFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message, confirmButtonText: "OK" });
    } finally {
        yield put(setKmsDocumentSaveLoading(false));
    }
}
